import { EnterOutlined } from "@ant-design/icons";
import { Input, InputNumber } from "antd";
import classNames from "classnames";
import React from "react";
import KeyCode from 'rc-util/lib/KeyCode';

interface EditableProps {
    prefixCls?: string;
    value: string;
    ['aria-label']?: string;
    onSave: (value: string) => void;
    onCancel: () => void;
    onEnd?: () => void;
    className?: string;
    style?: React.CSSProperties;
    maxLength?: number;
    autoSize?: boolean;
    enterIcon?: React.ReactNode;
    component?: string;
    onChange?: (value: string) => any
  }
  
const InlineEditableNumberInternal: React.FC<EditableProps & { singleLine: boolean }> = ({
    'aria-label': ariaLabel,
    className,
    style,
    maxLength,
    autoSize = true,
    value,
    onSave,
    onCancel,
    onEnd,
    onChange,
    singleLine
  }) => {
    const ref = React.useRef<any>();
    const prefixCls = "ant-typography";
  
    const inComposition = React.useRef(false);
    const lastKeyCode = React.useRef<number>();
  
    const [current, setCurrent] = React.useState(value);
  
    React.useEffect(() => {
      setCurrent(value);
    }, [value]);
  
    React.useEffect(() => {
      if (ref.current && ref.current.resizableTextArea) {
        const { textArea } = ref.current.resizableTextArea;
        textArea.focus();
        const { length } = textArea.value;
        textArea.setSelectionRange(length, length);
      }
    }, []);
  
    const onChangeInternal = (value: string | null) => {
      onChange?.(value!);
      setCurrent(value!);
    };
  
    const onCompositionStart = () => {
      inComposition.current = true;
    };
  
    const onCompositionEnd = () => {
      inComposition.current = false;
    };
  
    const confirmChange = () => {
      onSave(current);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = ({ keyCode }) => {
      // We don't record keyCode when IME is using
      if (inComposition.current) return;
  
      lastKeyCode.current = keyCode;
    };

    const onBlur: React.FocusEventHandler<HTMLInputElement> = () => {
      confirmChange();
    };
  
    const textAreaClassName = classNames(
      prefixCls,
      `${prefixCls}-edit-content`,
      className,
    );

    const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = ({
      keyCode,
      ctrlKey,
      altKey,
      metaKey,
      shiftKey,
    }) => {
      // Check if it's a real key
      if (
        lastKeyCode.current === keyCode &&
        !inComposition.current &&
        !ctrlKey &&
        !altKey &&
        !metaKey &&
        !shiftKey
      ) {
        if (keyCode === KeyCode.ENTER) {
          confirmChange();
          onEnd?.();
        } else if (keyCode === KeyCode.ESC) {
          onCancel();
        }
      }
    };
  
    return (
      <div className={textAreaClassName} style={{ width: "100%", ...style}}>
        <InputNumber
          ref={ref as any}
          maxLength={maxLength}
          value={current}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          type="number"
          precision={2}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onChange={onChangeInternal}
          aria-label={ariaLabel}
        />
      </div>
    );
  };
  
export default InlineEditableNumberInternal;
